/**
 * D-EZ-102 — THE NEWSSTAND. The Stock Car Dispatch as a place.
 *
 * Every race's edition as a back issue, a season-in-review edition for every
 * settled season, and a winter edition (retirements, the Hall of Fame, the
 * awards, sponsor news). Opening an edition prints it the way the game writes
 * it — no model is called to browse the back issues — unless a model already
 * wrote that edition, in which case the filed copy comes back. With a model
 * available, "Have the writer do it" asks for a fresh one, on request only.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CareerSeason } from '../../lib/careerRunner.ts';
import { collectSources, type LiveSeasonLike } from '../statsModel.ts';
import { leagueSeasons } from '../../domain/story/league.ts';
import { newsstand, type BackIssue, type IssueKind } from '../../domain/story/newsstand.ts';
import { paperFacts, type PaperFacts } from '../../domain/paper/paperFacts.ts';
import { withWinter } from '../../domain/paper/winterFacts.ts';
import { notebook, type Notebook } from '../../domain/paper/notebook.ts';
import { paperAvailable, readThePaper, type PaperArticle } from '../../lib/paper.ts';
import PaperModal from '../PaperModal.tsx';
import { WireModal } from '../Wire.tsx';

export interface LeagueInput {
  history: readonly CareerSeason[];
  career: CareerSeason | null;
  live?: LiveSeasonLike | null;
  leagueName: string;
}

interface Open {
  facts: PaperFacts;
  article: PaperArticle | null;
  reason: string | null;
  loading: boolean;
  notes: Notebook;
  standing: string;
  volume: string;
}

/**
 * The one reader every story screen uses: build an edition's facts, print it,
 * and hold the modal. `open(kind, season, race?)` and render `modal`.
 */
export function usePaperReader({ history, career, live = null, leagueName }: LeagueInput): {
  open: (kind: IssueKind, seasonNumber: number, raceNumber?: number) => void;
  modal: ReactNode;
} {
  const sources = useMemo(() => collectSources({ history: [...history], career, live }), [history, career, live]);
  const seasons = useMemo(() => leagueSeasons({ history, career, live }), [history, career, live]);
  const [paper, setPaper] = useState<Open | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  useEffect(() => {
    let on = true;
    void paperAvailable().then((ok) => on && setCanWrite(ok));
    return () => {
      on = false;
    };
  }, []);

  const factsFor = useCallback(
    (kind: IssueKind, seasonNumber: number, raceNumber?: number): PaperFacts | null => {
      const base = paperFacts({ leagueName, sources, seasonNumber, raceNumber: kind === 'race' ? raceNumber : undefined });
      if (!base || kind === 'wire') return null;
      return kind === 'race' ? base : withWinter(base, seasons, kind);
    },
    [leagueName, sources, seasons],
  );

  const print = useCallback(async (facts: PaperFacts, fresh: boolean) => {
    const standing =
      facts.edition === 'race' ? `Race ${facts.racesRun} of ${facts.racesInSeason}` : facts.edition === 'winter' ? `After season ${facts.season}` : `Season ${facts.season}`;
    const base = { facts, notes: notebook(facts), standing, volume: `Vol. ${facts.season}, No. ${facts.racesRun}` };
    setPaper({ ...base, article: null, reason: null, loading: true });
    const outcome = await readThePaper(facts, fresh ? { fresh: true } : { offline: true });
    setPaper({ ...base, article: outcome.article, reason: outcome.reason, loading: false });
  }, []);

  const open = useCallback(
    (kind: IssueKind, seasonNumber: number, raceNumber?: number) => {
      // The Wire is not an edition of the article; it has its own page (Wire.tsx).
      if (kind === 'wire') return;
      const facts = factsFor(kind, seasonNumber, raceNumber);
      if (facts) void print(facts, false);
    },
    [factsFor, print],
  );

  const modal = paper ? (
    <PaperModal
      article={paper.article}
      loading={paper.loading}
      reason={paper.reason}
      standing={paper.standing}
      notes={paper.notes}
      volume={paper.volume}
      onClose={() => setPaper(null)}
      onRewrite={canWrite ? () => void print(paper.facts, true) : undefined}
      rewriteLabel={paper.article?.source === 'ai' ? 'Write it again' : 'Have the writer do it'}
    />
  ) : null;

  return { open, modal };
}

export default function Newsstand(props: LeagueInput) {
  const { history, career, live } = props;
  const seasons = useMemo(() => leagueSeasons({ history, career, live }), [history, career, live]);
  const shelves = useMemo(() => newsstand(seasons), [seasons]);
  const { open, modal } = usePaperReader(props);
  const [season, setSeason] = useState<number | null>(null);
  /** D-EZ-109 — the season whose Wires are open, if any. */
  const [wireSeason, setWireSeason] = useState<number | null>(null);
  const shelf = useMemo(() => ({ history, career, live }), [history, career, live]);
  const shown = shelves.find((s) => s.seasonNumber === season) ?? shelves[0] ?? null;

  return (
    <section className="news" aria-labelledby="news-h">
      <header className="news__head">
        <p className="news__masthead">The Stock Car Dispatch</p>
        <h2 id="news-h" className="news__title">
          The newsstand
        </h2>
        <p className="news__sub">Every edition this league has printed. Pick one up to read it.</p>
      </header>

      {shelves.length === 0 ? (
        <p className="news__empty">Nothing on the stand yet — the first edition prints after the first race of a season.</p>
      ) : (
        <>
          {shelves.length > 1 && (
            <nav className="news__seasons" aria-label="Which season">
              {shelves.map((s) => (
                <button
                  key={s.seasonNumber}
                  type="button"
                  className={`seg${shown?.seasonNumber === s.seasonNumber ? ' is-on' : ''}`}
                  aria-pressed={shown?.seasonNumber === s.seasonNumber}
                  onClick={() => setSeason(s.seasonNumber)}
                >
                  Season {s.seasonNumber}
                </button>
              ))}
            </nav>
          )}
          {shown && (
            <ul className="news__rack" aria-label={`Season ${shown.seasonNumber} editions`}>
              {shown.issues.map((issue) => (
                <li key={issue.key}>
                  <IssueTile
                    issue={issue}
                    onOpen={() => (issue.kind === 'wire' ? setWireSeason(issue.seasonNumber) : open(issue.kind, issue.seasonNumber, issue.raceNumber))}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {modal}
      {wireSeason !== null && (
        <WireModal
          shelf={shelf}
          seasonNumber={wireSeason}
          onClose={() => setWireSeason(null)}
          onReadRace={(s, n) => {
            setWireSeason(null);
            open('race', s, n);
          }}
        />
      )}
    </section>
  );
}

function IssueTile({ issue, onOpen }: { issue: BackIssue; onOpen: () => void }) {
  return (
    <button type="button" className={`issue issue--${issue.kind}`} onClick={onOpen}>
      <span className="issue__label">{issue.label}</span>
      <span className="issue__headline">{issue.headline}</span>
      <span className="issue__sub">{issue.sub}</span>
    </button>
  );
}
